/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import {
  Typography,
  Table,
  InputNumber,
  Input,
  Button,
  Spin,
  message,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { get } from 'lodash';
import moment from 'moment';
import {
  fetchDsrLogPqt,
  updateDsrLogPqt,
} from '../../redux/projectPqtReports/actions';

const { Text } = Typography;

const PqtActualValuesPage = (props) => {
  const {
    selectedProject,
    selectedMainline,
    selectedDate,
    currentDsrLog,
    dsrLogPqt,
    isDsrLogPqtLoading,
    isDsrLogPqtUpdating,
    lastDsrLogPqtUpdateSuccess,
    onFetchDsrLogPqt,
    onUpdateDsrLogPqt,
    onCancel,
  } = props;

  const [localElements, setLocalElements] = useState([]);
  const [comments, setComments] = useState('');
  const [fetchKey, setFetchKey] = useState(0);
  const saveClickedRef = React.useRef(false);

  const projectId = get(selectedProject, 'id');
  const sowId = get(selectedMainline, 'id');

  const fetchPqtData = useCallback(() => {
    if (!currentDsrLog?.id || !projectId || !sowId || !selectedDate) return;
    const logDate = currentDsrLog.logDate || moment(selectedDate).format('YYYY-MM-DD');
    let userEmail = '';
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      userEmail = user.email || '';
    } catch (_) {}
    onFetchDsrLogPqt({
      logDate,
      userEmail,
      sowId: currentDsrLog.sowId ?? sowId,
      projectId,
    });
  }, [currentDsrLog?.id, currentDsrLog?.logDate, currentDsrLog?.sowId, projectId, sowId, selectedDate, onFetchDsrLogPqt]);

  // Fetch on every mount and whenever fetchKey changes (after save)
  useEffect(() => {
    fetchPqtData();
  }, [fetchPqtData, fetchKey]);

  // Sync local elements and comments from API response
  useEffect(() => {
    const elements = get(dsrLogPqt, 'elements', []);
    const sorted = [...elements].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    setLocalElements(
      sorted.map((el) => ({
        ...el,
        projectPqtReportElementId: el.projectPqtReportElementId ?? el.projectPqtElementId ?? el.id,
        id: el.projectPqtReportElementId ?? el.projectPqtElementId ?? el.id,
      }))
    );
    const existingComments = get(dsrLogPqt, 'comments', '');
    if (existingComments !== undefined && existingComments !== null) {
      setComments(String(existingComments));
    }
  }, [dsrLogPqt.elements, dsrLogPqt.pqt, dsrLogPqt.comments]);

  const handleActualDaysChange = useCallback((projectPqtReportElementId, value) => {
    setLocalElements((prev) =>
      prev.map((el) => {
        const id = el.projectPqtReportElementId ?? el.id;
        if (id !== projectPqtReportElementId) return el;
        const bidDays = Number(el.bidDays) || 0;
        const actualDays = value != null && value !== '' ? Number(value) : null;
        const variance = actualDays != null ? actualDays - bidDays : null;
        return { ...el, actualDays, variance };
      })
    );
  }, []);

  const getReportElementId = (el) => el.projectPqtReportElementId ?? el.projectPqtElementId ?? el.id;

  // Refetch after save completes and navigate back on success
  const prevUpdating = React.useRef(false);
  useEffect(() => {
    if (prevUpdating.current && !isDsrLogPqtUpdating) {
      setFetchKey((k) => k + 1);
      if (saveClickedRef.current && lastDsrLogPqtUpdateSuccess === true && onCancel) {
        saveClickedRef.current = false;
        onCancel();
      }
    }
    prevUpdating.current = isDsrLogPqtUpdating;
  }, [isDsrLogPqtUpdating, lastDsrLogPqtUpdateSuccess, onCancel]);

  const handleSave = useCallback(() => {
    if (!currentDsrLog?.id) {
      message.warning('DSR log not loaded. Please select date and SOW.');
      return;
    }
    const elements = localElements
      .filter((el) => {
        const actual = el.actualDays;
        return actual != null && actual !== '' && !Number.isNaN(Number(actual));
      })
      .map((el) => ({
        projectPqtElementId: el.projectPqtElementId ?? getReportElementId(el),
        actualDays: Number(el.actualDays),
      }));
    if (elements.length === 0) {
      message.info('Enter at least one actual days value to save.');
      return;
    }
    saveClickedRef.current = true;
    onUpdateDsrLogPqt({
      dsrLogId: currentDsrLog.id,
      elements,
      comments: comments.trim() || undefined,
    });
  }, [currentDsrLog?.id, localElements, comments, onUpdateDsrLogPqt]);

  const columns = [
    {
      title: 'PQT Activities',
      dataIndex: 'elementName',
      key: 'elementName',
      render: (text) => <Text strong={!!text}>{text || '—'}</Text>,
    },
    {
      title: 'Bid No. of Days',
      dataIndex: 'bidDays',
      key: 'bidDays',
      align: 'center',
      width: 100,
      render: (val) => (val != null ? Number(val) : '—'),
    },
    {
      title: 'Actual',
      key: 'actualDays',
      align: 'center',
      width: 120,
      render: (_, record) => {
        const id = getReportElementId(record);
        return (
          <InputNumber
            min={0}
            step={1}
            value={record.actualDays != null ? record.actualDays : undefined}
            placeholder="—"
            onChange={(v) => handleActualDaysChange(id, v)}
            style={{ width: '100%' }}
          />
        );
      },
    },
  ];

  const totals = localElements.reduce(
    (acc, el) => {
      const bid = Number(el.bidDays) || 0;
      const actual = el.actualDays != null && el.actualDays !== '' ? Number(el.actualDays) : null;
      acc.bid += bid;
      if (actual != null) acc.actual += actual;
      return acc;
    },
    { bid: 0, actual: 0 }
  );

  return (
    <div style={{ padding: 24, position: 'relative' }}>
      <Spin
        spinning={isDsrLogPqtLoading}
        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
        tip="Loading PQT data..."
        size="large"
      >
        <div style={{ marginBottom: 24 }}>
          <Table
            rowKey={(r) => r.projectPqtElementId ?? r.projectPqtReportElementId ?? r.elementName ?? r.sortOrder}
            columns={columns}
            dataSource={localElements}
            pagination={false}
            size="small"
            scroll={{ x: 600 }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <Text strong>Total</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="center">
                    <Text strong>{totals.bid}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="center">
                    <Text strong>{totals.actual}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Comments</Text>
          <Input.TextArea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Add comments (optional)"
            rows={4}
            style={{ resize: 'vertical' }}
          />
        </div>
      </Spin>

      <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000, display: 'flex', gap: 12 }}>
        <Button onClick={onCancel} size="large" shape="round">Cancel</Button>
        <Button
          onClick={handleSave}
          type="primary"
          size="large"
          shape="round"
          disabled={isDsrLogPqtUpdating || !currentDsrLog?.id || isDsrLogPqtLoading}
          loading={isDsrLogPqtUpdating}
        >
          Save & Next
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentDsrLog: get(state, 'dsrReducer.currentDsrLog'),
  dsrLogPqt: get(state, 'projectPqtReportsReducer.dsrLogPqt', { pqt: null, elements: [] }),
  isDsrLogPqtLoading: get(state, 'projectPqtReportsReducer.isDsrLogPqtLoading', false),
  isDsrLogPqtUpdating: get(state, 'projectPqtReportsReducer.isDsrLogPqtUpdating', false),
  lastDsrLogPqtUpdateSuccess: get(state, 'projectPqtReportsReducer.lastDsrLogPqtUpdateSuccess', null),
});

const mapDispatchToProps = {
  onFetchDsrLogPqt: fetchDsrLogPqt,
  onUpdateDsrLogPqt: updateDsrLogPqt,
};

export default connect(mapStateToProps, mapDispatchToProps)(PqtActualValuesPage);
